/**
 * Tooltip Overlay Component
 *
 * Interactive overlay for showing tooltips on hover/touch.
 */

import { useCallback, useRef } from "react";
import { CHART_CONFIG, CHART_COLORS } from "../constants";
import {
  getChartDimensions,
  indexToX,
  valueToY,
  formatDate,
  formatCurrency,
  formatPercentage,
} from "../utils";
import type { PerformanceDataPoint } from "../types";

interface TooltipOverlayProps {
  data: PerformanceDataPoint[];
  min: number;
  max: number;
  investedAmount: number;
  benchmarkName: string;
  activeIndex: number | null;
  onIndexChange: (index: number | null) => void;
}

/**
 * Interactive tooltip overlay
 */
export function TooltipOverlay({
  data,
  min,
  max,
  investedAmount,
  benchmarkName,
  activeIndex,
  onIndexChange,
}: TooltipOverlayProps) {
  const { innerWidth, innerHeight } = getChartDimensions();
  const svgRef = useRef<SVGGElement>(null);

  const handleInteraction = useCallback(
    (
      event: React.MouseEvent<SVGRectElement> | React.TouchEvent<SVGRectElement>
    ) => {
      const rect = (event.target as SVGRectElement).getBoundingClientRect();
      const clientX =
        "touches" in event ? event.touches[0]?.clientX ?? 0 : event.clientX;
      const relativeX = clientX - rect.left;
      const percentage = relativeX / rect.width;
      const index = Math.round(percentage * (data.length - 1));
      const clampedIndex = Math.max(0, Math.min(data.length - 1, index));
      onIndexChange(clampedIndex);
    },
    [data.length, onIndexChange]
  );

  const handleLeave = useCallback(() => {
    onIndexChange(null);
  }, [onIndexChange]);

  if (data.length === 0) return null;

  const activePoint = activeIndex !== null ? data[activeIndex] : null;
  const activeX =
    activeIndex !== null ? indexToX(activeIndex, data.length) : 0;
  const activeFundY = activePoint
    ? valueToY(activePoint.fundValue, min, max)
    : 0;
  const activeBenchmarkY = activePoint
    ? valueToY(activePoint.benchmarkValue, min, max)
    : 0;

  // Calculate percentage change from invested amount
  const fundChange = activePoint
    ? ((activePoint.fundValue - investedAmount) / investedAmount) * 100
    : 0;
  const benchmarkChange = activePoint
    ? ((activePoint.benchmarkValue - investedAmount) / investedAmount) * 100
    : 0;

  return (
    <g ref={svgRef} className="tooltip-overlay">
      {/* Invisible interaction area */}
      <rect
        x={CHART_CONFIG.paddingLeft}
        y={CHART_CONFIG.paddingTop}
        width={innerWidth}
        height={innerHeight}
        fill="transparent"
        onMouseMove={handleInteraction}
        onMouseLeave={handleLeave}
        onTouchMove={handleInteraction}
        onTouchEnd={handleLeave}
        style={{ cursor: "crosshair" }}
      />

      {/* Active point indicators */}
      {activeIndex !== null && activePoint && (
        <g className="active-indicators">
          {/* Vertical line */}
          <line
            x1={activeX}
            y1={CHART_CONFIG.paddingTop}
            x2={activeX}
            y2={CHART_CONFIG.paddingTop + innerHeight}
            stroke="currentColor"
            strokeOpacity={0.3}
            strokeWidth={1}
            strokeDasharray="4,4"
          />

          {/* Fund point */}
          <circle
            cx={activeX}
            cy={activeFundY}
            r={5}
            fill={CHART_COLORS.fund.stroke}
            stroke="white"
            strokeWidth={2}
          />

          {/* Benchmark point */}
          <circle
            cx={activeX}
            cy={activeBenchmarkY}
            r={4}
            fill={CHART_COLORS.benchmark.stroke}
            stroke="white"
            strokeWidth={2}
          />

          {/* Tooltip box */}
          <g
            transform={`translate(${
              activeX > CHART_CONFIG.width / 2 ? activeX - 120 : activeX + 10
            }, ${CHART_CONFIG.paddingTop + 10})`}
          >
            <rect
              x={0}
              y={0}
              width={110}
              height={68}
              rx={6}
              fill={CHART_COLORS.tooltip.bg}
            />
            <text
              x={8}
              y={16}
              className="text-[10px] font-medium"
              fill={CHART_COLORS.tooltip.text}
            >
              {formatDate(activePoint.date)}
            </text>
            <text
              x={8}
              y={32}
              className="text-[9px]"
              fill={CHART_COLORS.fund.stroke}
            >
              Fund: {formatCurrency(activePoint.fundValue)}
            </text>
            <text
              x={78}
              y={32}
              className="text-[8px]"
              fill={fundChange >= 0 ? "#10b981" : "#ef4444"}
            >
              {formatPercentage(fundChange)}
            </text>
            <text x={8} y={48} className="text-[9px]" fill="#9ca3af">
              {benchmarkName}: {formatCurrency(activePoint.benchmarkValue)}
            </text>
            <text
              x={8}
              y={62}
              className="text-[8px]"
              fill={benchmarkChange >= 0 ? "#10b981" : "#ef4444"}
            >
              {formatPercentage(benchmarkChange)}
            </text>
          </g>
        </g>
      )}
    </g>
  );
}
